import { c as _c } from "react/compiler-runtime";
import { postHostedIdentifySession, postHostedIdentifySessionVerify } from '@onefootprint/fetch-hosted';
import type { GetHostedUserAuthMethodsResponse, GetHostedUserTokenResponse } from '@onefootprint/request-types';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { DeviceInfo } from '@/idv2/utils/get-device';
import { getLogger } from '@/idv2/utils/logger';
import { runPasskeyChallenge } from './run-passkey-challenge';
const {
  logWarn
} = getLogger({
  name: 'idv2',
  location: 'use-step-up'
});
export type StepUpContext = {
  userToken: GetHostedUserTokenResponse;
  authMethods: GetHostedUserAuthMethodsResponse;
  deviceInfo: DeviceInfo;
};
type UseStepUpArgs = {
  authToken: string;
  stepUpContext: StepUpContext;
};
const isStepUpPossible = (deviceInfo: DeviceInfo, authMethods: GetHostedUserAuthMethodsResponse): boolean => {
  const passkeyAuthMethod = authMethods.find(method => method.kind === 'passkey' && method.isVerified);
  const clientSide = deviceInfo.hasSupportForWebauthn && (deviceInfo.type === 'desktop' ? passkeyAuthMethod?.isSyncablePasskey : true);
  return Boolean(passkeyAuthMethod) && Boolean(clientSide);
};
const isStepUpNeeded = (tokenData: GetHostedUserTokenResponse): boolean => {
  return !tokenData.scopes.includes('sensitive_profile');
};
const useStepUp = t0 => {
  const $ = _c(19);
  const {
    authToken,
    stepUpContext
  } = t0;
  const {
    userToken,
    authMethods,
    deviceInfo
  } = stepUpContext;
  let t1;
  if ($[0] !== authToken) {
    t1 = {
      mutationFn: async () => {
        const {
          data: t2
        } = await postHostedIdentifySession({
          body: {
            data: {},
            scope: "onboarding"
          },
          headers: {
            "X-Fp-Authorization": authToken
          }
        });
        const {
          token: identifyToken
        } = t2;
        await runPasskeyChallenge(identifyToken);
        const {
          data: t3
        } = await postHostedIdentifySessionVerify({
          headers: {
            "X-Fp-Authorization": identifyToken
          }
        });
        const {
          authToken: steppedUpAuthToken
        } = t3;
        return steppedUpAuthToken;
      }
    };
    $[0] = authToken;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const runPasskeyMutation = useMutation(t1);
  const isLoading = runPasskeyMutation.isPending;
  let t2;
  if ($[2] !== userToken) {
    t2 = isStepUpNeeded(userToken);
    $[2] = userToken;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const needsStepUp = t2;
  let t3;
  if ($[4] !== authMethods || $[5] !== deviceInfo) {
    t3 = isStepUpPossible(deviceInfo, authMethods);
    $[4] = authMethods;
    $[5] = deviceInfo;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const canStepUp = t3;
  let t4;
  if ($[7] !== authToken || $[8] !== canStepUp || $[9] !== deviceInfo.hasSupportForWebauthn || $[10] !== deviceInfo.type || $[11] !== needsStepUp || $[12] !== runPasskeyMutation) {
    t4 = async () => {
      if (!needsStepUp) {
        return authToken;
      }
      if (!canStepUp) {
        const errorMsg = `Cannot step up: ${deviceInfo.type} device, webauthn: ${deviceInfo.hasSupportForWebauthn}`;
        logWarn(errorMsg);
        throw new Error(errorMsg);
      }
      if (runPasskeyMutation.isPending) {
        throw new Error("Step-up already in progress");
      }
      return await runPasskeyMutation.mutateAsync();
    };
    $[7] = authToken;
    $[8] = canStepUp;
    $[9] = deviceInfo.hasSupportForWebauthn;
    $[10] = deviceInfo.type;
    $[11] = needsStepUp;
    $[12] = runPasskeyMutation;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const stepUp = t4;
  let t5;
  if ($[14] !== canStepUp || $[15] !== isLoading || $[16] !== needsStepUp || $[17] !== stepUp) {
    t5 = {
      needsStepUp,
      canStepUp,
      stepUp,
      isLoading
    };
    $[14] = canStepUp;
    $[15] = isLoading;
    $[16] = needsStepUp;
    $[17] = stepUp;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
};
export default useStepUp;